import { Fragment, useMemo, useState } from "react";

import { TextboxControlWithDatePicker } from "@/Common/Components/Inputs/TextboxControlWithDatePicker";
import { useEffectAfterMount } from "@/Common/CustomHooks/useEffectAfterMount";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { DateHelper } from "@/Common/Helpers/DateHelper";
import { DateTimeWithPrecision } from "@/Common/Models/DateTimeWithPrecision";
import { LifeStageDatePrecisionEnum } from "@/Common/Models/LifeStageDatePrecisionEnum";
import { LifeStageDateStatusEnum } from "@/Common/Models/LifeStageDateStatusEnum";
import { FlowsheetConstants } from "@/Flowsheets/FlowsheetConstants";
import { FlowsheetCareComponentValidatorHelper } from "@/Flowsheets/Helpers/FlowsheetCareComponentValidatorHelper";
import { CareComponent } from "@/Flowsheets/Models/CareElements/CareComponent";
import { careElementUnitMapper } from "@/Flowsheets/Models/CareElements/CareElementsUnitMapper";
import { CareElementType } from "@/Flowsheets/Models/CareElements/CareElementType";
import { SpirometryCareElement } from "@/Flowsheets/Models/CareElements/SpirometryCareElement";
import { SpirometryMeasurements } from "@/Flowsheets/Models/CareElements/SpirometryMeasurements";
import { NumberInputWithDecimals } from "../../ConfigureFlowsheetModal/CareElementsConfigurationParts/NumberInputWIthDecimals";
import CareComponentNotes from "./CareComponentParts/CareComponentNotes";
import { ICareComponentProps } from "./Types";

import styles from "./SpirometryCareComponent.module.scss";

interface SpirometryCareComponent extends CareComponent {
    CareElements: SpirometryCareElement[];
}

function ColumnHeader({ title, titlePosition }: Readonly<{ title: string, titlePosition: "center" | "left" }>): JSX.Element {
    const { getText } = useTranslation();
    return (
        <>
            {titlePosition === "center" && <p className={`${styles.subTitle} ${styles.offsetOne}`}>{title}</p>}

            {titlePosition === "left" && (
                <>
                    <p className={`${styles.subTitle}`}>{title}</p>
                    <p />
                </>
            )}

            <p className={`${styles.header} ${styles.offsetOne}`}>{getText("ValueLabel")}</p>
            <p className={`${styles.header} ${styles.offsetThree}`}>{getText("DateLabel")}</p>
        </>
    );
}

function SpirometryInput({
    keyName,
    state,
    elementName,
    isEditable,
    label,
    onChange,
    onCareComponentValidityChange,
    encounterDate,
    unit
}: Readonly<{
    keyName: string,
    elementName?: string,
    state: SpirometryMeasurements | null,
    isEditable: boolean,
    label?: string,
    onChange: (newState: SpirometryMeasurements) => void,
    onCareComponentValidityChange?: (id: string, isValid: boolean) => void,
    encounterDate: Date,
    unit: string
}>): JSX.Element {
    const [dateObject, setDateObject] = useState<DateTimeWithPrecision | null>(() => state?.Date
        ? new DateTimeWithPrecision(DateHelper.formatDateLuxon(state.Date, "yyyy-MM-dd"), "", LifeStageDatePrecisionEnum.Day)
        : new DateTimeWithPrecision(null, "", LifeStageDatePrecisionEnum.Day)
    );

    const [valueObject, setValueObject] = useState<number | null>(state?.Value ?? null);

    const autoPopulateValueWithPrecision = new DateTimeWithPrecision(DateHelper.formatDateLuxon(encounterDate, "yyyy-MM-dd"), "", LifeStageDatePrecisionEnum.Day);

    useEffectAfterMount(() => {
        const newState: SpirometryMeasurements = {
            Value: valueObject,
            Date: dateObject.value
        };

        onChange?.(newState);
        const isValid = FlowsheetCareComponentValidatorHelper.validateDateField(dateObject.text);
        onCareComponentValidityChange?.(elementName, isValid);
    }, [dateObject, valueObject]);

    const handleDateChange = (datePrecision: DateTimeWithPrecision): void => {
        if (datePrecision === null) {
            setDateObject(new DateTimeWithPrecision(null, "", LifeStageDatePrecisionEnum.Day));
            return;
        }

        if (
            datePrecision.status !== LifeStageDateStatusEnum.Valid &&
            !datePrecision.isDayPrecision &&
            datePrecision.value !== null
        ) {
            datePrecision.value = null;
        }

        setDateObject(datePrecision);
    };

    return (
        <>
            <p className={styles.label}>{label}</p>
            {isEditable ? (
                <NumberInputWithDecimals
                    key={`spirometry-input-${keyName}`}
                    numberValue={valueObject}
                    onChange={(strVal, numVal) => {
                        setValueObject(numVal);

                        if (numVal != null && !state?.Date) {
                            const dateValue = encounterDate;
                            handleDateChange(
                                dateValue
                                    ? new DateTimeWithPrecision(DateHelper.formatDateLuxon(dateValue, "yyyy-MM-dd"), "", LifeStageDatePrecisionEnum.Day)
                                    : autoPopulateValueWithPrecision
                            );
                        }
                    }}
                    dataCy={`spirometry-input-${elementName}`}
                />

            ) : (
                <p className={styles.offsetOne}>{valueObject}</p>
            )
            }
            <p className={styles.unit}>{unit}</p>
            {isEditable ? (
                <TextboxControlWithDatePicker
                    id={`date-${keyName}`}
                    key={`spirometry-date-${keyName}`}
                    expectedPrecisionMinimum={LifeStageDatePrecisionEnum.Day}
                    dateValue={dateObject.value}
                    textValue={dateObject.text}
                    onChange={handleDateChange}
                    maxDate={encounterDate}
                    width={120}
                    dataCy={`spirometry-date-${elementName}`}
                />
            ) : (
                <p className={styles.offsetThree}>{dateObject?.text}</p>
            )
            }
        </>
    );
}

function getPredictedCareElementNames(): string[] {
    return [
        FlowsheetConstants.Spirometry.ForcedExpiratoryVolumeInOneSecondCareElementName,
        FlowsheetConstants.Spirometry.ForcedVitalCapacityCareElementName,
        FlowsheetConstants.Spirometry.ForcedExpiratoryVolumeInOneSecondComparedToPredictedNormalValueCareElementName
    ];
}

const SpirometryCareComponent = React.memo(
    ({ isEditable, state, mutateState, encounterDate, onCareComponentValidityChange }: ICareComponentProps<SpirometryCareComponent>): JSX.Element => {
        const { getText } = useTranslation();

        const predictedCareElements = useMemo(() => state.CareElements.filter(p => getPredictedCareElementNames().includes(p.Name)), [state.CareElements]);

        if (state?.CareElements == null
            || state.CareElements.length === 0
            || state.CareElements[0].CareElementType !== CareElementType.Spirometry) {
            return null;
        }

        function getLabel(name: string): string {
            return getText(`Flowsheets_SpirometryCareComponentLabel_${name}`);
        }

        function handleValueChange(currentElement: SpirometryCareElement, newValue: SpirometryCareElement): void {
            mutateState(s => {
                const index = s.CareElements.findIndex(p => p.Name === currentElement.Name);
                if (index !== -1) {
                    const updatedElements = [...s.CareElements];
                    updatedElements[index] = newValue;
                    s.CareElements = updatedElements;
                }
            });
        }

        return (
            <div>
                <h1 className={styles.title}>{getText("Flowsheets_BeforeAfterBronchodilator")}</h1>

                <div className={styles.spirometryContainer}>
                    <div className={styles.column}>
                        <ColumnHeader title={getText("Flowsheets_BeforePuff")} titlePosition="center" />
                        {state.CareElements.map(element => (
                            <Fragment key={`spirometry-${state.Id}-${element.Name}-before`}>
                                <SpirometryInput
                                    label={getLabel(element.Name)}
                                    elementName={`${getLabel(element.Name)}-before`}
                                    state={element.BeforePuffMeasurements}
                                    onChange={newElement => handleValueChange(element, { ...element, BeforePuffMeasurements: newElement })}
                                    keyName={`spirometry-${state.Id}-${element.Name}-before`}
                                    isEditable={isEditable}
                                    onCareComponentValidityChange={onCareComponentValidityChange}
                                    encounterDate={encounterDate}
                                    unit={careElementUnitMapper[element.Name] || ""}
                                />
                            </Fragment>
                        ))}
                    </div>
                    <div className={styles.column}>
                        <ColumnHeader title={getText("Flowsheets_AfterPuff")} titlePosition="center" />
                        {state.CareElements.map(element => (
                            <Fragment key={`spirometry-${state.Id}-${element.Name}-after`}>
                                <SpirometryInput
                                    state={element.AfterPuffMeasurements}
                                    elementName={`${getLabel(element.Name)}-after`}
                                    onChange={newElement => handleValueChange(element, { ...element, AfterPuffMeasurements: newElement })}
                                    keyName={`spirometry-${state.Id}-${element.Name}-after`}
                                    isEditable={isEditable}
                                    onCareComponentValidityChange={onCareComponentValidityChange}
                                    encounterDate={encounterDate}
                                    unit={careElementUnitMapper[element.Name] || ""}
                                />
                            </Fragment>
                        ))}
                    </div>
                    <div className={`${styles.column} ${styles.marginLeft}`}>
                        <ColumnHeader title={getText("Flowsheets_PredictedValues")} titlePosition="left" />
                        {predictedCareElements.map(element => (
                            <Fragment key={`spirometry-${state.Id}-${element.Name}-predicted`}>
                                <SpirometryInput
                                    label={getLabel(element.Name.concat(FlowsheetConstants.Spirometry.PredictedCareElemenSuffix))}
                                    elementName={`${getLabel(element.Name)}-predicted`}
                                    state={element.PredictedMeasurements}
                                    onChange={newElement => handleValueChange(element, { ...element, PredictedMeasurements: newElement })}
                                    keyName={`spirometry-${state.Id}-${element.Name}-predicted`}
                                    isEditable={isEditable}
                                    onCareComponentValidityChange={onCareComponentValidityChange}
                                    encounterDate={encounterDate}
                                    unit={careElementUnitMapper[FlowsheetConstants.Spirometry.PredictedCareElemenSuffix]}
                                />
                            </Fragment>
                        ))}
                    </div>
                </div>

                <CareComponentNotes
                    id={`spirometryNotes-${state.Id}`}
                    value={state.Note}
                    isEditable={isEditable}
                    onTextChange={newValue => mutateState(s => s.Note = newValue)}
                    dataCy="SpirometryNotes"
                />
            </div>
        );
    }
);

export { SpirometryCareComponent };
